import { Fragment } from "react";
import { useSelector } from "react-redux";
import * as Recharts from "recharts"

import {
    PinIcon,
    PinSlashIcon
} from "@primer/octicons-react";
import {
    PencilIcon,
    TrashIcon
} from "@heroicons/react/24/outline";

import {
    IBarDateChartData,
    IBarTagChartData,
    IChart,
    IChartFunnelData,
    ICountTableChartData,
    ILineDateChartData,
    INumberChartData,
    IStatsRange,
    IWidget,
    IWidgetDateDistributionChartValuesType,
} from "../lib/backend/extractions.types.generated";
import {
    classNames,
    niceRound,
    prettyNumber
} from "../lib/utils";
import { selectUser } from "../lib/scraper.slice";

import { IconButton } from "./Button";
import { StatsRange } from "./StatsRange";

const FONT_COLOR = "#4b5563";
const BAR_COLOR = "#001565";
const VERTICAL_BAR_COLOR = "#001565";
const BAR_COLORS = ["#001565", "#004AF5", "#74A3FF", "#7584BD", "#CCD8FF", "#00E3B0", "#3FB9AF"];

const VALIDATION_KEYS = ["pass", "fail"];
const VALIDATION_VALUE_NAME_MAP: Record<string, string> = {
    "pass": "Automatic",
    "fail": "Manual",
}
const VALIDATION_COLORS = ["#00E3B0", "#001565"];
const YES_NO_KEYS = ["yes", "no"];
const YES_NO_COLORS = ["#001565", "#FF1534"];
const ONBOARDING_KEYS = ["in_progress", "correct"];
const ONBOARDING_COLORS = ["#001565", "#00E3B0"];

function fixLegendValue(value: string) {
    // capitalize first letter and remove underscores and lowercase the rest
    const no_underscores = value.replace(/_/g, " ");
    return no_underscores.charAt(0).toUpperCase() + no_underscores.slice(1).toLowerCase();
}

type StackedBarChartProps = {
    data: IBarDateChartData[];
    aspect: number;
    values_type: IWidgetDateDistributionChartValuesType | "onboarding";
}

export function StackedBarChart(props: StackedBarChartProps) {
    const { data, aspect, values_type } = props;

    const values = values_type === "validation" ? VALIDATION_KEYS :
        values_type === "yes_no" ? YES_NO_KEYS :
            values_type === "onboarding" ? ONBOARDING_KEYS :
                Object.keys(data[0]).filter(key => key !== "date");
    const colors = values_type === "validation" ? VALIDATION_COLORS :
        values_type === "yes_no" ? YES_NO_COLORS :
            values_type === "onboarding" ? ONBOARDING_COLORS :
                BAR_COLORS;

    const getValueDisplayName = (value: string) => {
        if (values_type === "validation" && value in VALIDATION_VALUE_NAME_MAP) {
            return VALIDATION_VALUE_NAME_MAP[value];
        }
        return fixLegendValue(value);
    };

    return (
        <div className="w-full h-full text-sm p-4">
            <Recharts.ResponsiveContainer width="100%" aspect={aspect}>
                <Recharts.BarChart data={data} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                    <Recharts.XAxis dataKey="date" fontSize={12} stroke={FONT_COLOR} />
                    <Recharts.YAxis fontSize={12} width={40} stroke={FONT_COLOR} />
                    <Recharts.Tooltip
                        contentStyle={{ color: FONT_COLOR, fontSize: "12px" }}
                        formatter={(value: any, name: string) => [value, getValueDisplayName(name)]}
                    />
                    <Recharts.Legend formatter={(value) => <span style={{ color: FONT_COLOR, fontSize: "12px" }}>{getValueDisplayName(value)}</span>} />
                    {values.map((key, idx) => (<Recharts.Bar key={idx} dataKey={key} fill={colors[idx % colors.length]} stackId="a" />))}
                </Recharts.BarChart>
            </Recharts.ResponsiveContainer>
        </div>
    );
}

type BarChartProps = {
    data: IBarDateChartData[];
    aspect: number;
}

export function BarChart(props: BarChartProps) {
    const { data, aspect } = props;

    if (data.length === 0) {
        return <div className="w-full h-full text-sm p-4">
            <Recharts.ResponsiveContainer width="100%" aspect={aspect}>
                <Recharts.ComposedChart data={data} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                    <Recharts.XAxis dataKey="date" fontSize={12} stroke={FONT_COLOR} />
                    <Recharts.YAxis fontSize={12} width={40} stroke={FONT_COLOR} />
                    <Recharts.Tooltip contentStyle={{ color: FONT_COLOR, fontSize: "12px" }} />
                    <Recharts.Legend formatter={(value) => <span style={{ color: FONT_COLOR, fontSize: "12px" }}>{fixLegendValue(value)}</span>} />
                </Recharts.ComposedChart>
            </Recharts.ResponsiveContainer>
        </div>
    }

    const tags = Object.keys(data[0]).filter(key => key !== "date");

    return (
        <div className="w-full h-full text-sm p-4">
            <Recharts.ResponsiveContainer width="100%" aspect={aspect}>
                <Recharts.ComposedChart data={data} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                    <Recharts.XAxis dataKey="date" fontSize={12} stroke={FONT_COLOR} />
                    <Recharts.YAxis fontSize={12} width={40} stroke={FONT_COLOR} />
                    <Recharts.Tooltip contentStyle={{ color: FONT_COLOR, fontSize: "12px" }} />
                    <Recharts.Legend formatter={(value) => <span style={{ color: FONT_COLOR, fontSize: "12px" }}>{fixLegendValue(value)}</span>} />
                    {tags.map((tag, index) => (
                        <Recharts.Bar
                            key={tag}
                            dataKey={tag}
                            name={tag}
                            fill={BAR_COLORS[index % BAR_COLORS.length]}
                        />
                    ))}
                </Recharts.ComposedChart>
            </Recharts.ResponsiveContainer>
        </div>
    );
}

type BarTagChartProps = {
    data: IBarTagChartData[];
    aspect: number;
    tag_width?: number;
}

export function BarTagChart(props: BarTagChartProps) {
    const { data, aspect, tag_width = 120 } = props;

    return (
        <div className="w-full h-full text-sm p-4">
            <Recharts.ResponsiveContainer width="100%" aspect={aspect}>
                <Recharts.BarChart data={data} margin={{ top: 10, right: 20, left: 40, bottom: 0 }} layout="vertical">
                    <Recharts.YAxis dataKey="tag" type="category" fontSize={12} width={tag_width} stroke={FONT_COLOR} />
                    <Recharts.XAxis type="number" fontSize={12} stroke={FONT_COLOR} />
                    <Recharts.Tooltip contentStyle={{ color: FONT_COLOR, fontSize: "12px" }} />
                    <Recharts.Legend formatter={(value) => <span style={{ color: FONT_COLOR, fontSize: "12px" }}>{fixLegendValue(value)}</span>} />
                    <Recharts.Bar dataKey="count" fill={VERTICAL_BAR_COLOR} label={{ position: "right", fontSize: 12, fill: FONT_COLOR }} />
                </Recharts.BarChart>
            </Recharts.ResponsiveContainer>
        </div>
    );
}

type SingleBarChartProps = {
    data: IBarDateChartData[];
    aspect: number;
}

function SingleBarChart(props: SingleBarChartProps) {
    const { data, aspect } = props;

    return (
        <div className="w-full h-full text-sm p-4">
            <Recharts.ResponsiveContainer width="100%" aspect={aspect}>
                <Recharts.ComposedChart data={data} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                    <Recharts.XAxis dataKey="date" fontSize={12} stroke={FONT_COLOR} />
                    <Recharts.YAxis fontSize={12} width={40} stroke={FONT_COLOR} />
                    <Recharts.Tooltip contentStyle={{ color: FONT_COLOR, fontSize: "12px" }} />
                    <Recharts.Legend formatter={(value) => <span style={{ color: FONT_COLOR, fontSize: "12px" }}>{fixLegendValue(value)}</span>} />
                    <Recharts.Bar dataKey="count" fill={BAR_COLOR} />
                </Recharts.ComposedChart>
            </Recharts.ResponsiveContainer>
        </div>
    );
}

type LinesProps = {
    data: ILineDateChartData[];
    aspect: number;
}

function Lines(props: LinesProps) {
    const { data, aspect } = props;

    // user niceRound to round the values
    const rounded_data = data.map(row => ({
        ...row,
        ...Object.fromEntries(Object.entries(row).map(([key, value]) => [key, key === "date" ? value : niceRound(Number(value))])),
    }));

    return (
        <div className="w-full h-full text-sm p-4">
            <Recharts.ResponsiveContainer width="100%" aspect={aspect}>
                <Recharts.LineChart data={rounded_data} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                    <Recharts.XAxis dataKey="date" fontSize={12} stroke={FONT_COLOR} />
                    <Recharts.YAxis fontSize={12} width={40} stroke={FONT_COLOR} />
                    <Recharts.Tooltip contentStyle={{ color: FONT_COLOR, fontSize: "12px" }} />
                    <Recharts.Legend formatter={(value) => <span style={{ color: FONT_COLOR, fontSize: "12px" }}>{fixLegendValue(value)}</span>} />
                    {Object.keys(data[0]).filter(key => key !== "date").map((key, index) => (
                        <Recharts.Line key={key} dataKey={key} stroke={BAR_COLORS[index % BAR_COLORS.length]} />
                    ))}
                </Recharts.LineChart>
            </Recharts.ResponsiveContainer>
        </div>
    );
}

type CountTableProps = {
    data: ICountTableChartData[];
    decimals?: number;
    prefix?: string;
    suffix?: string;
}

function CountTable(props: CountTableProps) {
    const { data, decimals, prefix, suffix } = props;

    const user = useSelector(selectUser);

    return <div className="w-full" style={{ aspectRatio: '2/1' }}>
        <div className="h-full overflow-y-auto">
            <table className="min-w-full divide-y divide-gray-200">
                <tbody className="bg-white divide-y divide-gray-200">
                    {data.map((row, index) => (
                        <tr key={index} className="hover:bg-gray-50">
                            <td className="px-3 py-2 text-sm text-gray-900 w-1">{index + 1}</td>
                            <td className="px-3 py-2 text-sm text-gray-900">{row.pivots.join(" / ")}</td>
                            <td className="px-3 py-2 text-sm text-gray-500 font-medium text-right">
                                {`${prefix ?? ""}${prettyNumber(row.count, decimals ?? 2, undefined, user.number_format_locale)}${suffix ?? ""}`}
                            </td>
                            {row.trend !== undefined && <td className={classNames("pl-3 pr-6 py-2 text-xs text-left w-1 whitespace-nowrap", row.trend > 0 ? "text-mint-600" : "text-torch_red-400")}>
                                {row.trend > 0 ? "+" : ""}{prettyNumber(row.trend, decimals ?? 2, undefined, user.number_format_locale)}
                            </td>}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    </div>;
}

type FunnelProps = {
    data: IChartFunnelData[];
    decimals?: number;
    prefix?: string;
    suffix?: string;
}

function Funnel(props: FunnelProps) {
    const { data, decimals, prefix, suffix } = props;

    const user = useSelector(selectUser);

    const height = data.length === 5 ? 1409 : data.length === 4 ? 1154 : data.length === 3 ? 879 : 614;

    return <div>
        <svg viewBox={`0 0 2682 ${height}`} fill="none" xmlns="http://www.w3.org/2000/svg">
            {/* Stage 1 */}
            <path d="M1991 181L992 181" stroke="#B3B3B3" stroke-width="3" stroke-dasharray="12 6" />
            <circle cx="2006.5" cy="180.5" r="12" stroke="#B3B3B3" stroke-width="3" />
            <path d="M132.164 58H1810.84C1823.58 58 1831.87 72.1587 1826.17 84.1864L1738.69 268.725C1729.32 288.508 1710.16 301 1689.2 301H253.803C232.847 301 213.692 288.508 204.311 268.725L116.834 84.1864C111.132 72.1587 119.419 58 132.164 58Z" fill="#001565" />
            <text x="971" y="220" textAnchor="middle" fill="white" fontSize="100" fontWeight={700} fontFamily="Open Sauce Sans, sans-serif">
                {`${prefix ?? ""}${prettyNumber(data[0].value, decimals ?? 2, undefined, user.number_format_locale)}${suffix ?? ""}`}
            </text>
            <foreignObject x="2100" y="140" width="500" height="250">
                <div style={{ color: "#4B5563", fontSize: "64px", fontFamily: "Open Sauce Sans, sans-serif", lineHeight: "1.1" }}>
                    {data[0].stage}
                </div>
            </foreignObject>

            {/* Stage 2 */}
            <path d="M1991 446L992 446" stroke="#B3B3B3" stroke-width="3" stroke-dasharray="12 6" />
            <circle cx="2006.5" cy="445.5" r="12" stroke="#B3B3B3" stroke-width="3" />
            <path d="M261.159 324H1681.84C1694.58 324 1702.87 338.159 1697.17 350.186L1609.69 534.725C1600.32 554.508 1581.16 567 1560.2 567H382.801C361.845 567 342.689 554.508 333.309 534.725L245.834 350.186C240.132 338.159 248.419 324 261.159 324Z" fill="#7584BD" />
            <text x="971" y="485" textAnchor="middle" fill="white" fontSize="100" fontWeight={700} fontFamily="Open Sauce Sans, sans-serif">
                {`${prefix ?? ""}${prettyNumber(data[1].value, decimals ?? 2, undefined, user.number_format_locale)}${suffix ?? ""}`}
            </text>
            <foreignObject x="2100" y="405" width="500" height="250">
                <div style={{ color: "#4B5563", fontSize: "64px", fontFamily: "Open Sauce Sans, sans-serif", lineHeight: "1.1" }}>
                    {data[1].stage}
                </div>
            </foreignObject>

            {data.length > 2 && <Fragment>
                {/* Stage 3 */}
                <path d="M1991 711L992 711" stroke="#B3B3B3" stroke-width="3" stroke-dasharray="12 6" />
                <circle cx="2006.5" cy="710.5" r="12" stroke="#B3B3B3" stroke-width="3" />
                <path d="M388.145 589H1554.86C1567.58 589 1575.87 603.159 1570.17 615.186L1482.77 799.725C1473.4 819.508 1454.26 832 1433.32 832H509.683C488.744 832 469.605 819.508 460.233 799.725L372.832 615.186C367.135 603.159 375.415 589 388.145 589Z" fill="#004AF5" />
                <text x="971" y="750" textAnchor="middle" fill="white" fontSize="100" fontWeight={700} fontFamily="Open Sauce Sans, sans-serif">
                    {`${prefix ?? ""}${prettyNumber(data[2].value, decimals ?? 2, undefined, user.number_format_locale)}${suffix ?? ""}`}
                </text>
                <foreignObject x="2100" y="680" width="500" height="250">
                    <div style={{ color: "#4B5563", fontSize: "64px", fontFamily: "Open Sauce Sans, sans-serif", lineHeight: "1.1" }}>
                        {data[2].stage}
                    </div>
                </foreignObject>
            </Fragment>}

            {data.length > 3 && <Fragment>
                {/* Stage 4 */}
                <path d="M1991 977L992 977" stroke="#B3B3B3" stroke-width="3" stroke-dasharray="12 6" />
                <circle cx="2006.5" cy="976.5" r="12" stroke="#B3B3B3" stroke-width="3" />
                <path d="M515.14 855H1428.86C1441.59 855 1449.86 869.159 1444.17 881.186L1356.79 1065.73C1347.42 1085.51 1328.28 1098 1307.35 1098H636.649C615.715 1098 596.581 1085.51 587.21 1065.73L499.832 881.186C494.136 869.159 502.414 855 515.14 855Z" fill="#74A3FF" />
                <text x="971" y="1015" textAnchor="middle" fill="white" fontSize="100" fontWeight={700} fontFamily="Open Sauce Sans, sans-serif">
                    {`${prefix ?? ""}${prettyNumber(data[3].value, decimals ?? 2, undefined, user.number_format_locale)}${suffix ?? ""}`}
                </text>
                <foreignObject x="2100" y="935" width="500" height="250">
                    <div style={{ color: "#4B5563", fontSize: "64px", fontFamily: "Open Sauce Sans, sans-serif", lineHeight: "1.1" }}>
                        {data[3].stage}
                    </div>
                </foreignObject>
            </Fragment>}

            {data.length > 4 && <Fragment>
                {/* Stage 5 */}
                <path d="M1991 1242L992 1242" stroke="#B3B3B3" stroke-width="3" stroke-dasharray="12 6" />
                <circle cx="2006.5" cy="1241.5" r="12" stroke="#B3B3B3" stroke-width="3" />
                <path d="M643.159 1120H1299.84C1312.58 1120 1320.87 1134.16 1315.17 1146.19L1227.69 1330.72C1218.32 1350.51 1199.16 1363 1178.2 1363H764.8C743.844 1363 724.688 1350.51 715.308 1330.72L627.834 1146.19C622.132 1134.16 630.419 1120 643.159 1120Z" fill="#CCD8FF" />
                <text x="971" y="1280" textAnchor="middle" fill="white" fontSize="100" fontWeight={700} fontFamily="Open Sauce Sans, sans-serif">
                    {`${prefix ?? ""}${prettyNumber(data[4].value, decimals ?? 2, undefined, user.number_format_locale)}${suffix ?? ""}`}
                </text>
                <foreignObject x="2100" y="1200" width="500" height="250">
                    <div style={{ color: "#4B5563", fontSize: "64px", fontFamily: "Open Sauce Sans, sans-serif", lineHeight: "1.1" }}>
                        {data[4].stage}
                    </div>
                </foreignObject>
            </Fragment>}
        </svg>
    </div>;
}

type ChartProps = {
    widget: IWidget;
    chart: IChart;
    is_wide?: boolean;
}

export function Chart({ widget, chart, is_wide = false }: ChartProps) {
    const aspect = is_wide ? 4 : 2;

    if (chart.chart_type === "stacked_bar" && chart.data_type === "bar_date") {
        const values_type = (widget.type === "date_distribution_chart") ? widget.details.type : "all_values";
        return <StackedBarChart data={chart.data} aspect={aspect} values_type={values_type} />;
    }
    if (chart.chart_type === "bar" && chart.data_type === "bar_date") {
        return <BarChart data={chart.data} aspect={aspect} />;
    }
    if (chart.chart_type === "single_bar" && chart.data_type === "bar_date") {
        return <SingleBarChart data={chart.data} aspect={aspect} />;
    }
    if (chart.chart_type === "bar_tag" && chart.data_type === "bar_tag") {
        return <BarTagChart data={chart.data} aspect={aspect} />;
    }
    if (chart.chart_type === "lines" && chart.data_type === "line_date") {
        return <Lines data={chart.data} aspect={aspect} />;
    }
    if (chart.chart_type === "count_table" && chart.data_type === "count_table") {
        return <CountTable data={chart.data} decimals={chart.decimals} prefix={chart.prefix} suffix={chart.suffix} />;
    }

    if (chart.chart_type === "funnel" && chart.data_type === "funnel") {
        return <Funnel data={chart.data} decimals={chart.decimals} prefix={chart.prefix} suffix={chart.suffix} />;
    }

    return null;
}

type NumbersProps = {
    data: INumberChartData;
    decimals?: number;
    color?: "blue" | "green" | "red" | "black";
    prefix?: string;
    suffix?: string;
    is_pinned?: boolean;
    is_editable?: boolean;
    range?: IStatsRange;
    range_local_storage_key?: string;
    onTogglePin?: () => void;
    onEdit?: () => void;
    onRemove?: () => void;
    onRangeChange?: (range: IStatsRange) => void;
}

function Numbers(props: NumbersProps) {
    const { data, decimals = 2, color = "black", prefix = "", suffix = "", is_pinned, is_editable, range, range_local_storage_key, onTogglePin, onEdit, onRemove, onRangeChange } = props;

    const user = useSelector(selectUser);

    const metrics: { name: string, stat: string, color: "blue" | "green" | "red" | "black" }[] = [];
    for (const [key, value] of Object.entries(data)) {
        const rounded_value = niceRound(value);
        metrics.push({
            name: key,
            stat: prettyNumber(rounded_value, decimals, undefined, user.number_format_locale),
            color: color
        });
    }

    return <Fragment>
        {metrics.map((metric) => (
            <div key={metric.name} className="bg-white p-4 rounded-lg shadow ring-1 ring-black ring-opacity-5">
                <div className="flex flex-col relative">
                    <div className="flex justify-between items-center">
                        <div className="flex flex-row items-center gap-x-2 truncate" title={metric.name}>
                            <span className="text-xs font-medium uppercase text-gray-500 truncate">{metric.name}</span>
                        </div>
                        {range !== undefined && onRangeChange !== undefined && range_local_storage_key !== undefined && <div className="flex items-center gap-x-4">
                            <StatsRange
                                range={range}
                                local_storage_key={range_local_storage_key}
                                setRange={onRangeChange} />
                        </div>}
                    </div>
                    <span className={`text-2xl font-bold mt-1 truncate ${metric.color === "red" ? "text-torch_red-500" : metric.color === "green" ? "text-mint-600" : metric.color === "blue" ? "text-sea_blue-500" : "text-gray-900"}`}>
                        <span className="text-gray-900 text-lg">{prefix}</span>{metric.stat}<span className="text-gray-900 text-lg">{suffix}</span>
                    </span>
                    <div className="flex flex-row gap-x-1 absolute bottom-0 right-0">
                        {onTogglePin !== undefined && <IconButton icon={is_pinned ? PinIcon : PinSlashIcon} disabled={!is_editable} onClick={onTogglePin} size="tiny" />}
                        {onEdit !== undefined && <IconButton icon={PencilIcon} disabled={!is_editable} onClick={onEdit} size="tiny" />}
                        {onRemove !== undefined && <IconButton icon={TrashIcon} disabled={!is_editable} onClick={onRemove} size="tiny" />}
                    </div>
                </div>
            </div>
        ))}
    </Fragment>;
}

type IndicatorProps = {
    chart: IChart;
    is_pinned?: boolean;
    is_editable?: boolean;
    range?: IStatsRange;
    range_local_storage_key?: string;
    onTogglePin?: () => void;
    onEdit?: () => void;
    onRemove?: () => void;
    onRangeChange?: (range: IStatsRange) => void;
}

export function Indicator(props: IndicatorProps) {
    const { chart, is_pinned, is_editable, range, range_local_storage_key, onTogglePin, onEdit, onRemove, onRangeChange } = props;
    if (chart.chart_type === "numbers" && chart.data_type === "numbers") {
        return <Numbers
            data={chart.data}
            decimals={chart.decimals}
            color={chart.color}
            prefix={chart.prefix}
            suffix={chart.suffix}
            is_pinned={is_pinned}
            is_editable={is_editable}
            range={range}
            range_local_storage_key={range_local_storage_key}
            onTogglePin={onTogglePin}
            onEdit={onEdit}
            onRemove={onRemove}
            onRangeChange={onRangeChange}
        />;
    }
    return null;
}